import { IDataSetColumn } from '@adaptiva/core/common';
import { BaseFilterCondition, search } from '@adaptiva/core/filters';
import { ISortConfig, ISortEvent, SchemaAndRows, TableSortService } from '@adaptiva/core/tables';
import { IUiWidgetConfig, UiService } from '@adaptiva/core/ui';
import { BaseWidgetComponent, provideBaseWidget, TextConfig, WidgetService } from '@adaptiva/core/widgets';
import { ICpeRow, ICpeWebView } from '@adaptiva/metadata/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal, signal, WritableSignal } from '@angular/core';
import { ICpeWebViewRow } from './interfaces';

@Component({
	standalone: false,
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'ada-cpe-web-view-table',
	templateUrl: 'cpe-web-view-table.component.html',
	providers: [
		TableSortService,
		WidgetService,
		provideBaseWidget(() => CpeWebViewTableComponent),
	]
})
export class CpeWebViewTableComponent extends BaseWidgetComponent implements AfterViewInit {

	public readonly cpeWebViews: InputSignal<ICpeWebView[]> = input();

	public readonly data: Signal<SchemaAndRows<ICpeWebViewRow> | null> = computed(() => {
		const _data: ICpeWebView[] | undefined = this.cpeWebViews();

		if (_data == null)
			return null;

		const _rows: ICpeWebViewRow[] = this.filterRows(_data, this.filter(), this.sort());

		return new SchemaAndRows<ICpeWebViewRow>(this.schema, _rows, _rows.length);
	});

	public readonly filter: WritableSignal<BaseFilterCondition> = signal(null);

	public readonly sort: WritableSignal<ISortConfig> = signal(null);

	protected readonly schema: IDataSetColumn[] = [
		new TextConfig({
			columnName: 'productName',
			columnDisplay: 'Product Name',
			type: 'string'
		}),
		new TextConfig({
			columnName: 'vendorName',
			columnDisplay: 'Vendor Name',
			type: 'string'
		}),
		{
			columnName: 'versionEndExcluding',
			type: 'string'
		},
		{
			columnName: 'versionEndIncluding',
			type: 'string'
		},
		{
			columnName: 'versionStartExcluding',
			type: 'string'
		},
		{
			columnName: 'versionStartIncluding',
			type: 'string'
		}
	];

	protected readonly cellWidgetConfigs: IUiWidgetConfig[] = [
		new TextConfig({
			columnName: 'productName',
			columnDisplay: 'Product Name',
			type: 'string'
		}),
		new TextConfig({
			columnName: 'vendorName',
			columnDisplay: 'Vendor Name',
			type: 'string'
		})
	];

	protected readonly ui: UiService = inject(UiService);

	protected readonly tableSortService: TableSortService = inject(TableSortService);

	public onFilter(_filter: BaseFilterCondition): void {
		this.filter.set(_filter);
	}

	public onSort(_event: ISortEvent): void {
		this.sort.set(_event.sortConfig);
	}

	/**
	 * Syncs both filtering and sorting of the array
	 * NOTE this will re-filter the array each time. An option is to store a filtered array but would duplicate potentially large datasets
	 * @param _filter The filter condition to be applied or undefined.
	 * @param _sortConfig The sort config to be applied or undefined
	 * @returns The filtered and sorted products
	 */
	protected filterRows(_webViews: ICpeWebView[], _filter: BaseFilterCondition, _sortConfig: ISortConfig): ICpeWebViewRow[] {
		let _rows: ICpeWebViewRow[] = ICpeRow.addRowInfo<ICpeWebView>(_webViews);

		if (_filter)
			_rows = search(_rows, _filter);
		if (_sortConfig)
			_rows = this.tableSortService.sort(_rows, _sortConfig);

		return _rows;
	}

}
